import React, { useState, useEffect } from 'react';
import { Gift, Building2, Calendar, Plus, RefreshCw, Trash2, Eye, Image } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { getRebates, deleteRebate } from '../../api/wholesale/rebates';
import { getCompanies } from '../../api/wholesale/companies';
import RebateFormModal from '../../components/wholesale/RebateFormModal';
import RebateDetailModal from '../../components/wholesale/RebateDetailModal';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const WholesaleRebatesPage = () => {
  const [searchParams] = useSearchParams();
  const [rebates, setRebates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRebate, setSelectedRebate] = useState(null);
  const [editingRebate, setEditingRebate] = useState(null);

  // Filters - initialize from URL params
  const [filters, setFilters] = useState({
    company_id: searchParams.get('company_id') || '',
    rebate_type: '',
    rebate_year: new Date().getFullYear()
  });

  useEffect(() => {
    fetchRebates();
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchRebates();
  }, [filters]);

  const fetchRebates = async () => {
    try {
      setLoading(true);
      const response = await getRebates(filters);
      setRebates(response.data || []);
    } catch (error) {
      console.error('Error fetching rebates:', error);
      toast.error('Failed to load rebates');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data || response || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation(); // Prevent row click
    if (!window.confirm('Delete this rebate?')) {
      return;
    }

    try {
      await deleteRebate(id);
      toast.success('Rebate deleted');
      fetchRebates();
    } catch (error) {
      console.error('Error deleting rebate:', error);
      toast.error(error.response?.data?.message || 'Failed to delete rebate');
    }
  };

  const handleRowClick = (rebate) => {
    setSelectedRebate(rebate);
    setIsDetailModalOpen(true);
  };

  const handleEdit = (rebate) => {
    setIsDetailModalOpen(false);
    setEditingRebate(rebate);
    setIsFormModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingRebate(null);
    setIsFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditingRebate(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPeriod = (rebate) => {
    if (rebate.rebate_type === 'monthly') {
      return `${MONTH_NAMES[rebate.rebate_month - 1]} ${rebate.rebate_year}`;
    }
    return `Year ${rebate.rebate_year}`;
  };

  // Calculate totals
  const totalRebates = rebates.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
  const monthlyTotal = rebates
    .filter(r => r.rebate_type === 'monthly')
    .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
  const yearlyTotal = rebates
    .filter(r => r.rebate_type === 'yearly')
    .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);

  // Generate year options (current year to 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    yearOptions.push(y);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
            <Gift className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Rebates & Incentives</h1>
            <p className="text-gray-500">Track monthly and yearly rebates from companies</p>
          </div>
        </div>
        <button
          onClick={handleAddNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add Rebate
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Rebates</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalRebates)}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Gift className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">{rebates.length} records</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Monthly Rebates</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(monthlyTotal)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {rebates.filter(r => r.rebate_type === 'monthly').length} records
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Yearly Rebates</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(yearlyTotal)}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {rebates.filter(r => r.rebate_type === 'yearly').length} records
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <select
              value={filters.company_id}
              onChange={(e) => setFilters(prev => ({ ...prev, company_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">All Companies</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.company_name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[150px]">
            <select
              value={filters.rebate_type}
              onChange={(e) => setFilters(prev => ({ ...prev, rebate_type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">All Types</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          <div className="flex-1 min-w-[120px]">
            <select
              value={filters.rebate_year}
              onChange={(e) => setFilters(prev => ({ ...prev, rebate_year: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">All Years</option>
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <button
            onClick={fetchRebates}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Rebates Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
          </div>
        ) : rebates.length === 0 ? (
          <div className="text-center py-12">
            <Gift className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No rebates found</h3>
            <p className="text-gray-500 mb-4">Add your first rebate to start tracking</p>
            <button
              onClick={handleAddNew}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4" />
              Add Rebate
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Receipt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rebates.map((rebate) => (
                  <tr 
                    key={rebate.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleRowClick(rebate)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{rebate.company_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        rebate.rebate_type === 'monthly'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {rebate.rebate_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {formatPeriod(rebate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-emerald-600">
                      {formatCurrency(rebate.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      {rebate.received_date
                        ? new Date(rebate.received_date).toLocaleDateString('en-NG')
                        : '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {rebate.receipt_url ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                          <Image className="w-3 h-3" />
                          Yes
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <RebateFormModal
        isOpen={isFormModalOpen}
        onClose={handleCloseFormModal}
        rebate={editingRebate}
        onSuccess={fetchRebates}
      />

      <RebateDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        rebate={selectedRebate}
        onEdit={handleEdit}
        onDeleted={fetchRebates}
      />
    </div>
  );
};

export default WholesaleRebatesPage;
